
import { defineComponent, onBeforeMount, reactive } from 'vue'

import NewModal from '@/common/new-components/NewModal/index.vue'
import NewInput from '@/ui-kit/NewInput/index.vue'
import NewSelect from '@/ui-kit/NewSelect/index.vue'

import useVuelidate from '@vuelidate/core'
import { required, requiredIf } from '@vuelidate/validators'

import { UpdatePreTripPayload } from '@/core/types/tms-graph.types'
import { tmsGraph } from '@/core/realisations/tms-graph.realisation'

import { TripComment, TripTMS } from '@/new-services/types/tms'
import tmsAPI from '@/new-services/tms.service'

import useNotifications from '@/common/composable/useNotifications'

const ISSUE_BY_LOGIST_REASON_ID = 39
const ISSUE_BY_WAREHOUSE_REASON_ID = 40
const EARLY_SEND_REASON_ID = 41

export default defineComponent({
    components: {
        NewModal,
        NewInput,
        NewSelect,
    },
    props: {
        id: {
            type: Number,
            required: true,
        },
        dateFrom: {
            type: String,
            default: null,
        },
        dateTo: {
            type: String,
            default: null
        }
    },
    emits: ['close', 'success'],
    setup(props, { emit }) {
        const notifications = useNotifications()

        const state = reactive({
            date: null as string | null,
            commentId: null as number | null,
            commentInfo: null as string | null,
            tripId: null as number | null,
            is_loading: false,
        })

        const comments = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as TripComment[],
        })

        const trips = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as TripTMS[],
        })

        const state_form$ = useVuelidate(
            {
                date: { required },
                commentId: { required },
                commentInfo: { required: requiredIf(() => state.commentId === ISSUE_BY_LOGIST_REASON_ID || state.commentId === ISSUE_BY_WAREHOUSE_REASON_ID) },
                tripId: { required: requiredIf(() => state.commentId === EARLY_SEND_REASON_ID) }
            },
            state
        )

        const loadComments = async () => {
            try {
                comments.is_loading = true

                const response = await tmsAPI.getPreTripComments()
                comments.list = response.data.data

                comments.is_failed = false
            } catch (error) {
                console.error(error)
                comments.is_failed = true
            } finally {
                comments.is_loading = false
            }
        }

        const getFormattedDate = (): string => {
            const date = new Date(Date.now())
            return date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0') + '-' + String(date.getDate()).padStart(2, '0')
        }

        const loadTrips = async () => {
            resetTrips()
            state.tripId = null

            try {
                trips.is_loading = true

                const response = await tmsAPI.getTrips({
                    page: 1,
                    limit: 9999,
                    dateFrom: state.date as string,
                    dateTo: state.date as string
                })

                trips.list = response.data.data

                trips.is_failed = false
            } catch (error) {
                console.error(error)
                trips.is_failed = true
            } finally {
                trips.is_loading = false
            }
        }

        const resetTrips = () => {
            trips.is_loading = false
            trips.is_failed = false
            trips.list = []
        }

        const onSubmit = async () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            try {
                state.is_loading = true

                const payload: UpdatePreTripPayload = {
                    commentId: state.commentId as number,
                    date: state.date as string,
                    commentInfo: state.commentInfo,
                    tripId: state.tripId
                }

                await tmsGraph.updatePreTrip(props.id, payload)

                notifications.notify({ title: 'Предрейс сохранен', type: 'success' })
                onSuccess()
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При редактировании предрейса произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        const onSuccess = () => {
            emit('success')
        }

        const onClose = () => {
            emit('close')
        }

        onBeforeMount(() => {
            state.date = getFormattedDate()
            loadComments()
        })

        const onDateChange = (date: string) => {
            if (state.commentId && state.commentId === EARLY_SEND_REASON_ID) {
                if (Boolean(date)) {
                    const selectedDate = new Date(date)

                    if (selectedDate.getFullYear() >= 2000) {
                        loadTrips()
                    }
                }
            }
        }

        const onReasonChange = (reasonId: number | null) => {
            resetTrips()

            state.commentInfo = null
            state.tripId = null

            if (reasonId) {
                if (reasonId === EARLY_SEND_REASON_ID) {
                    state.date = null
                    return
                }
            }
        }

        return {
            ISSUE_BY_LOGIST_REASON_ID,
            ISSUE_BY_WAREHOUSE_REASON_ID,
            EARLY_SEND_REASON_ID,
            state,
            state_form$,
            comments,
            trips,
            onSubmit,
            onClose,
            onReasonChange,
            onDateChange
        }
    },
})
